import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { SyutSelect } from 'components/ui/SyutSelect';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import notificationService from 'services/notificationService';
import { SyutSearchGrid, type SyutSearchGridColumn, type SyutSearchGridFilter } from 'components/ui/SyutSearchGrid';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { sampleServiceService, sampleCategoryService, statusService } from 'api/services';
import type { SampleServiceDto, SampleServiceScope, StatusDto } from 'api/types';

/**
 * ============================================================================
 * SERVICE MASTER MODAL COMPONENTS - REFERENCE IMPLEMENTATION
 * ============================================================================
 * Aligned with SampleServiceDto / SampleCategoryDto from the backend API.
 * No mock data — all lookups (categories) fetched asynchronously via API.
 * ============================================================================
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/** ServiceMasterRecord is an alias for SampleServiceDto */
export type ServiceMasterRecord = SampleServiceDto;

/** Internal form data — aligns with CreateSampleServiceRequest */
export interface SampleMasterFormData {
  code: string;
  serviceName: string;
  description: string;
  categoryId: string;
  uom: string;
  scope: SampleServiceScope | '';
  effectiveFrom: string;
  effectiveTo: string;
  rate: string;       // string for input; parsed to number on save
  statusCode: string;
}

const INITIAL_FORM_DATA: SampleMasterFormData = {
  code: '',
  serviceName: '',
  description: '',
  categoryId: '',
  uom: '',
  scope: 'Entity',
  effectiveFrom: '',
  effectiveTo: '',
  rate: '',
  statusCode: 'ACTIVE',
};

const SCOPE_OPTIONS = [
  { value: 'Entity', label: 'Entity' },
  { value: 'Enterprise', label: 'Enterprise' },
  { value: 'BusinessUnit', label: 'Business Unit' },
];

// ============================================================================
// SEARCHABLE DROPDOWN (generic reusable utility component)
// ============================================================================

interface SearchableDropdownProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
}

export const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  label
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <div className="form-group">
      {label && <label className="form-label">{label}</label>}
      <div ref={dropdownRef} style={{ position: 'relative' }}>
        <div
          className="form-trigger"
          onClick={() => setIsOpen(!isOpen)}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
        >
          <span style={{ color: value ? 'var(--text-primary)' : 'var(--text-muted)' }}>
            {value || placeholder}
          </span>
          <IconSystem.chevronDown size={16} />
        </div>

        {isOpen && (
          <div className="dropdown-panel" style={{ maxHeight: '300px' }}>
            <div style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-color)' }}>
              <div style={{ position: 'relative' }}>
                <IconSystem.search size={16} style={{
                  position: 'absolute', left: '0.625rem', top: '50%',
                  transform: 'translateY(-50%)', color: 'var(--text-muted)'
                }} />
                <input
                  type="text"
                  className="dropdown-search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Type to search..."
                  autoFocus
                />
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', maxHeight: '250px' }}>
              {filteredOptions.length === 0 ? (
                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                  No options found
                </div>
              ) : (
                filteredOptions.map((option, index) => (
                  <div
                    key={index}
                    onClick={() => { onChange(option); setIsOpen(false); setSearchQuery(''); }}
                    style={{
                      padding: '0.625rem 1rem', cursor: 'pointer', fontSize: '0.85rem',
                      color: 'var(--text-primary)',
                      background: value === option ? '#f8fafc' : 'transparent',
                      fontWeight: value === option ? 600 : 400
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = '#f8fafc'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = value === option ? '#f8fafc' : 'transparent'; }}
                  >
                    {option}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// SAMPLE MASTER FORM MODAL
// ============================================================================

export interface SampleMasterFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy';
  /** Record to populate in edit/copy mode. Null/undefined in create mode. */
  service?: ServiceMasterRecord | null;
  onSave: () => void;
  onClose: () => void;

  openedBy?: string;  // DEBUG identifier

  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy', instanceId?: string, serviceCode?: string) => void;
  createInstanceId?: string;
  copyInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

export const SampleMasterFormModal: React.FC<SampleMasterFormModalProps> = ({
  isOpen,
  mode,
  service,
  onSave,
  onClose,
  openedBy,
  tabId,
  moduleName,
  onRestoreModal,
  createInstanceId,
  copyInstanceId,
  minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  const [formData, setFormData] = useState<SampleMasterFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const serviceRef = useRef(service);
  serviceRef.current = service;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const copyInstanceIdRef = useRef(copyInstanceId);
  copyInstanceIdRef.current = copyInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [concurrencyConflict, setConcurrencyConflict] = useState<{ open: boolean; message: string }>({
    open: false, message: ''
  });

  // Populate form on open; skip on restore to preserve user edits
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const currentMode = modeRef.current;
    const currentService = serviceRef.current;
    let data: SampleMasterFormData;
    if ((currentMode === 'edit' || currentMode === 'copy') && currentService) {
      data = {
        code: currentMode === 'copy' ? '' : currentService.code,
        serviceName: currentMode === 'copy' ? `${currentService.serviceName} (Copy)` : currentService.serviceName,
        description: currentService.description || '',
        categoryId: currentService.categoryId as string || '',
        uom: currentService.uom || '',
        scope: currentService.scope || 'Entity',
        effectiveFrom: currentMode === 'copy' ? new Date().toISOString().split('T')[0] : currentService.effectiveFrom,
        effectiveTo: currentMode === 'copy' ? '' : currentService.effectiveTo || '',
        rate: currentService.rate !== null && currentService.rate !== undefined ? String(currentService.rate) : '',
        statusCode: currentMode === 'copy' ? 'ACTIVE' : (currentService.statusCode || 'ACTIVE'),
      };
    } else {
      data = { ...INITIAL_FORM_DATA, effectiveFrom: new Date().toISOString().split('T')[0] };
    }
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const loadStatusOptions = useCallback(async (inputValue: string) => {
    const statuses: StatusDto[] = await statusService.getByType('GENERAL');
    const filtered = inputValue
      ? statuses.filter(s => s.statusName.toLowerCase().includes(inputValue.toLowerCase()))
      : statuses;
    return filtered.map(s => ({ value: s.statusCode, label: s.statusName }));
  }, []);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentCopyInstanceId = copyInstanceIdRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else if (currentMode === 'copy') {
      modalId = `${tabId}-form-modal-copy-${currentCopyInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentFormData.code || 'new'}`;
    }

    let title: string;
    const code = currentFormData.code?.trim();
    const name = currentFormData.serviceName?.trim();
    if (currentMode === 'create') {
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else if (currentMode === 'copy') {
      const idx = currentCopyInstanceId || '1';
      title = code ? `Copy - ${code} (${idx})` : name ? `Copy - ${name} (${idx})` : `Copy (${idx})`;
    } else {
      title = `Edit - ${currentFormData.code || 'Service'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(currentFormData) !== savedSnapshot : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        onRestoreModal?.(
          currentMode,
          currentMode === 'create' ? currentCreateInstanceId : currentMode === 'copy' ? currentCopyInstanceId : undefined,
          currentMode !== 'create' ? currentFormData.code : undefined,
        );
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const getFieldError = (field: string) => fieldErrors[field];
  const clearFieldError = (field: string) => {
    setFieldErrors(prev => { if (!prev[field]) return prev; const n = { ...prev }; delete n[field]; return n; });
  };

  // Load category options asynchronously — no static data, always fresh from API
  const loadCategoryOptions = useCallback(async (inputValue: string) => {
    const res = await sampleCategoryService.getAll({
      searchTerm: inputValue || undefined,
      pageSize: inputValue ? 50 : 500,
    });
    return res.data.map(cat => ({ value: cat.id as string, label: `${cat.code} - ${cat.name}` }));
  }, []);

  const handleSave = useCallback(async () => {
    const errors: Record<string, string> = {};
    if (!formData.code.trim()) errors.code = 'Code is required';
    if (!formData.serviceName.trim()) errors.serviceName = 'Service Name is required';
    if (!formData.effectiveFrom) errors.effectiveFrom = 'Effective From is required';
    if (formData.effectiveTo && formData.effectiveFrom && formData.effectiveTo < formData.effectiveFrom) {
      errors.effectiveTo = 'Effective To cannot be earlier than Effective From';
    }
    if (formData.rate && isNaN(Number(formData.rate))) errors.rate = 'Rate must be a valid number';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    // Async code uniqueness check
    if (formData.code.trim()) {
      try {
        const excludeId = mode === 'edit' ? service?.id : undefined;
        const available = await sampleServiceService.isCodeAvailable(formData.code.trim(), excludeId);
        if (!available) {
          setFieldErrors({ code: 'This code already exists. Please use a different code.' });
          return;
        }
      } catch {
        // Validation failed — let server enforce uniqueness
      }
    }

    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData, mode, service]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const payload = {
        code: formData.code.trim(),
        serviceName: formData.serviceName.trim(),
        description: formData.description.trim() || undefined,
        categoryId: formData.categoryId || undefined,
        uom: formData.uom.trim() || undefined,
        scope: formData.scope || undefined,
        effectiveFrom: formData.effectiveFrom,
        effectiveTo: formData.effectiveTo || undefined,
        rate: formData.rate ? parseFloat(formData.rate) : undefined,
        statusCode: formData.statusCode,
      };

      if (mode === 'edit' && service) {
        await sampleServiceService.update(service.id as string, {
          ...payload,
          rowVersion: service.rowVersion,
        });
      } else {
        await sampleServiceService.create(payload);
      }

      setIsSaving(false);
      const label = mode === 'create' ? 'created' : mode === 'copy' ? 'copied' : 'updated';
      notificationService.toast.success(`Service "${formData.serviceName}" has been ${label} successfully.`);
      onSave();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const message = err?.response?.data?.message || err?.message;
      if (status === 409 && message) {
        setConcurrencyConflict({ open: true, message });
      } else {
        notificationService.toast.error(message || 'An error occurred while saving. Please try again.');
      }
    }
  }, [formData, mode, service, onSave, onClose]);

  const handleRefreshFromConflict = useCallback(async () => {
    if (!service?.id) { setConcurrencyConflict({ open: false, message: '' }); return; }
    setIsRefreshing(true);
    try {
      const res = await sampleServiceService.getById(service.id);
      const fresh = res.data;
      const freshData: SampleMasterFormData = {
        code: fresh.code,
        serviceName: fresh.serviceName,
        description: fresh.description || '',
        categoryId: fresh.categoryId as string || '',
        uom: fresh.uom || '',
        scope: fresh.scope || 'Entity',
        effectiveFrom: fresh.effectiveFrom,
        effectiveTo: fresh.effectiveTo || '',
        rate: fresh.rate !== null && fresh.rate !== undefined ? String(fresh.rate) : '',
        statusCode: fresh.statusCode || 'ACTIVE',
      };
      setFormData(freshData);
      initialSnapshotRef.current = JSON.stringify(freshData);
      setConcurrencyConflict({ open: false, message: '' });
      notificationService.toast.info('Form refreshed with latest data. Please review and save again.');
    } catch {
      notificationService.toast.error('Could not refresh from server. Please close and re-open the record.');
    } finally {
      setIsRefreshing(false);
    }
  }, [service]);

  if (process.env.NODE_ENV === 'development') {
    console.log('SampleMasterFormModal opened', { openedBy: openedBy || 'Unknown', mode });
  }

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.8)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', zIndex: 10, borderRadius: 'inherit'
          }}>
            <div style={{
              width: '2.5rem', height: '2.5rem', border: '3px solid var(--border-color)',
              borderTopColor: 'var(--primary)', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }} />
            <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              {mode === 'create' ? 'Creating service...' : mode === 'copy' ? 'Copying service...' : 'Saving changes...'}
            </p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        <div className="modal-header">
          <h2 className="modal-title">
            {mode === 'create' && 'Add New Service'}
            {mode === 'edit' && 'Edit Service'}
            {mode === 'copy' && 'Copy Service'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-grid-2">
            {/* Code */}
            <div className="form-group">
              <label className="form-label">Code <span className="text-danger">*</span></label>
              <input
                type="text"
                data-field="code"
                className={`form-input${getFieldError('code') ? ' form-field-error' : ''}`}
                value={formData.code}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }));
                  clearFieldError('code');
                }}
                placeholder="e.g., SVC001"
                readOnly={mode === 'edit'}
                style={mode === 'edit' ? { background: 'var(--bg-secondary)' } : {}}
              />
              {getFieldError('code') && <div className="form-error-message">{getFieldError('code')}</div>}
            </div>

            {/* Status dropdown */}
            <div className="form-group">
              <SyutSelect
                label="Status"
                isAsync
                loadOptions={loadStatusOptions}
                defaultOptions={true}
                cacheOptions={false}
                value={formData.statusCode}
                onChange={(val) => setFormData(prev => ({ ...prev, statusCode: (val as string) || 'ACTIVE' }))}
                placeholder="Select status..."
                menuPortalTarget={document.body}
              />
            </div>
          </div>

          {/* Service Name */}
          <div className="form-group">
            <label className="form-label">Service Name <span className="text-danger">*</span></label>
            <input
              type="text"
              data-field="serviceName"
              className={`form-input${getFieldError('serviceName') ? ' form-field-error' : ''}`}
              value={formData.serviceName}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, serviceName: e.target.value }));
                clearFieldError('serviceName');
              }}
              placeholder="e.g., Business Consulting"
            />
            {getFieldError('serviceName') && <div className="form-error-message">{getFieldError('serviceName')}</div>}
          </div>

          {/* Description */}
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Detailed description of the service"
              rows={3}
            />
          </div>

          <div className="form-grid-2">
            {/* Category (async) */}
            <div className="form-group">
              <SyutSelect
                label="Category"
                isAsync
                loadOptions={loadCategoryOptions}
                defaultOptions={true}
                cacheOptions={false}
                value={formData.categoryId}
                onChange={(val) => setFormData(prev => ({ ...prev, categoryId: (val as string) || '' }))}
                placeholder="Select category..."
                isClearable
                size="md"
                menuPortalTarget={document.body}
              />
            </div>

            {/* UOM */}
            <div className="form-group">
              <label className="form-label">Unit of Measure (UOM)</label>
              <input
                type="text"
                className="form-input"
                value={formData.uom}
                onChange={(e) => setFormData(prev => ({ ...prev, uom: e.target.value }))}
                placeholder="e.g., Hour, Each, Kg"
              />
            </div>

            {/* Scope */}
            <div className="form-group">
              <SyutSelect
                label="Scope"
                options={SCOPE_OPTIONS}
                value={formData.scope}
                onChange={(val) => setFormData(prev => ({ ...prev, scope: (val as SampleServiceScope) || 'Entity' }))}
                placeholder="Select scope..."
                isClearable={false}
                size="md"
                menuPortalTarget={document.body}
              />
            </div>

            {/* Rate */}
            <div className="form-group">
              <label className="form-label">Rate</label>
              <input
                type="number"
                data-field="rate"
                className={`form-input${getFieldError('rate') ? ' form-field-error' : ''}`}
                value={formData.rate}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, rate: e.target.value }));
                  clearFieldError('rate');
                }}
                placeholder="0.00"
                min="0"
                step="0.01"
              />
              {getFieldError('rate') && <div className="form-error-message">{getFieldError('rate')}</div>}
            </div>

            {/* Effective From */}
            <div className="form-group">
              <label className="form-label">Effective From <span className="text-danger">*</span></label>
              <input
                type="date"
                data-field="effectiveFrom"
                className={`form-input${getFieldError('effectiveFrom') ? ' form-field-error' : ''}`}
                value={formData.effectiveFrom}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, effectiveFrom: e.target.value }));
                  clearFieldError('effectiveFrom');
                }}
              />
              {getFieldError('effectiveFrom') && <div className="form-error-message">{getFieldError('effectiveFrom')}</div>}
            </div>

            {/* Effective To */}
            <div className="form-group">
              <label className="form-label">Effective To</label>
              <input
                type="date"
                data-field="effectiveTo"
                className={`form-input${getFieldError('effectiveTo') ? ' form-field-error' : ''}`}
                value={formData.effectiveTo}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, effectiveTo: e.target.value }));
                  clearFieldError('effectiveTo');
                }}
              />
              {getFieldError('effectiveTo') && <div className="form-error-message">{getFieldError('effectiveTo')}</div>}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
            <IconSystem.save size={16} />
            {mode === 'edit' ? 'Save Changes' : 'Create Service'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'create' ? 'Confirm Create' : mode === 'copy' ? 'Confirm Create' : 'Confirm Update'}
        message={
          mode === 'create'
            ? `Create service "${formData.serviceName}"?`
            : mode === 'copy'
              ? `Create service as "${formData.serviceName}"?`
              : `Save changes to "${formData.serviceName}"?`
        }
        confirmLabel={mode === 'create' ? 'Create' : mode === 'copy' ? 'Create' : 'Save'}
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />

      <ConfirmDialog
        open={concurrencyConflict.open}
        title="Record Modified by Another User"
        message={concurrencyConflict.message || 'This record was modified by another user while your form was open. Refresh to load their changes, or keep your current values.'}
        confirmLabel={isRefreshing ? 'Refreshing...' : 'Refresh from Server'}
        cancelLabel="Keep My Changes"
        variant="default"
        loading={isRefreshing}
        onConfirm={handleRefreshFromConflict}
        onCancel={() => setConcurrencyConflict({ open: false, message: '' })}
      />
    </>
  );
};

// ============================================================================
// SERVICE MASTER VIEW MODAL
// ============================================================================

export interface ServiceMasterViewModalProps {
  isOpen: boolean;
  service: ServiceMasterRecord | null;
  onClose: () => void;
  onEdit?: (service: ServiceMasterRecord) => void;

  // Minimize-to-bar support
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (serviceCode: string) => void;
}

const ViewField: React.FC<{ label: string; value?: string | null; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <label className="form-label">{label}</label>
    <div style={{ padding: '0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--form-border-color)', fontSize: '0.875rem', minHeight: '36px' }}>
      {value || <span style={{ color: 'var(--text-muted)' }}>—</span>}
    </div>
  </div>
);

export const ServiceMasterViewModal: React.FC<ServiceMasterViewModalProps> = ({
  isOpen,
  service,
  onClose,
  onEdit,
  tabId,
  moduleName,
  onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !service) return;
    minimize({
      id: `${tabId}-view-modal-${service.code}`,
      title: `View - ${service.code}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(service.code),
    });
    onClose();
  }, [tabId, moduleName, service, minimize, onClose, onRestoreView]);

  if (!isOpen || !service) return null;

  const scopeLabel = service.scope === 'BusinessUnit' ? 'Business Unit' : service.scope;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg">
        <div className="modal-header">
          <h2 className="modal-title">Service Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <ViewField label="Code" value={service.code} />
            <div>
              <label className="form-label">Status</label>
              <div style={{ paddingTop: '4px' }}>
                <span
                  className="badge"
                  style={service.statusColor ? { background: service.statusColor, color: '#fff' } : undefined}
                >
                  {service.statusName || service.statusCode || '—'}
                </span>
              </div>
            </div>
            <ViewField label="Service Name" value={service.serviceName} fullWidth />
            <ViewField label="Description" value={service.description} fullWidth />
            <ViewField label="Category" value={service.categoryName || service.categoryCode} />
            <ViewField label="UOM" value={service.uom} />
            <ViewField label="Scope" value={scopeLabel} />
            <ViewField label="Rate" value={service.rate !== null && service.rate !== undefined ? service.rate.toLocaleString() : null} />
            <ViewField label="Effective From" value={service.effectiveFrom} />
            <ViewField label="Effective To" value={service.effectiveTo} />
            <ViewField label="Created By" value={service.createdByName || service.createdBy} />
            <ViewField label="Created At" value={service.createdAt ? new Date(service.createdAt).toLocaleString() : null} />
            {service.updatedAt && (
              <>
                <ViewField label="Updated By" value={service.updatedByName || service.updatedBy} />
                <ViewField label="Updated At" value={new Date(service.updatedAt).toLocaleString()} />
              </>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
          {onEdit && (
            <button className="btn btn-primary" onClick={() => { onClose(); onEdit(service); }}>
              <IconSystem.edit size={16} />
              Edit
            </button>
          )}
        </div>
      </div>
    </>
  );
};

// ============================================================================
// PRODUCT SEARCH POPUP — generic ERP-style F2/Ctrl+L lookup
// ============================================================================

export interface ProductSearchPopupProps<T = any> {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (record: T) => void;
  title?: string;
  data: T[];
  columns?: SyutSearchGridColumn<T>[];
  filters?: SyutSearchGridFilter[];
  rowKey?: keyof T | string;
  searchPlaceholder?: string;
  width?: string;
  height?: string;
  enableRecentlyUsed?: boolean;
  recentlyUsedKey?: string;
  zIndex?: number;
}

export function ProductSearchPopup<T extends Record<string, any>>({
  isOpen,
  onClose,
  onSelect,
  title = 'Search',
  data,
  columns,
  filters,
  rowKey = 'id',
  searchPlaceholder = 'Search...',
  width = '900px',
  height = '600px',
  enableRecentlyUsed = true,
  recentlyUsedKey = 'product-search',
  zIndex = 10050,
}: ProductSearchPopupProps<T>) {
  const defaultColumns: SyutSearchGridColumn<T>[] = useMemo(() => [
    { key: 'code', header: 'Code', width: '120px', sortable: true },
    { key: 'serviceName', header: 'Service Name', width: '220px', sortable: true },
    { key: 'categoryName', header: 'Category', width: '150px', sortable: true },
    { key: 'uom', header: 'UOM', width: '80px', sortable: true },
    {
      key: 'isActive',
      header: 'Status',
      width: '90px',
      render: (value: boolean) => (
        <span className={`badge badge-${value ? 'active' : 'inactive'}`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      )
    },
  ], []);

  return (
    <SyutSearchGrid<T>
      isOpen={isOpen}
      onClose={onClose}
      onSelect={onSelect}
      title={title}
      columns={columns || defaultColumns}
      data={data}
      rowKey={rowKey as string}
      filters={filters}
      enableRecentlyUsed={enableRecentlyUsed}
      recentlyUsedKey={recentlyUsedKey}
      searchPlaceholder={searchPlaceholder}
      showRowNumbers
      width={width}
      height={height}
      zIndex={zIndex}
    />
  );
}
